import React, { useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ImagePlus, Plus, Trash2 } from 'lucide-react';
import { ILMSAlert } from '../../../components/ILMSAlert';
import { ILMSButton } from '../../../components/ILMSButton';
import { ILMSInput, ILMSSelect } from '../../../components/ILMSForm';
import { mediaUrl, PHOTO_SLOTS, type GalleryPhoto } from '../../../utils/site';
import { PhotoPicker, useUpload } from './PhotoPicker';
import { SaveBar } from './SaveBar';
import { useSiteDraft } from './useSiteDraft';

const GROUPS = Array.from(new Set(PHOTO_SLOTS.map((s) => s.group)));

// Every photo place on the website and login page, and the website's
// Gallery - add, caption, move and remove photos with no developer.
export function PhotosPanel() {
  const { draft, set, save, saving, error, saved, dirty, canEdit } = useSiteDraft();
  const [newSection, setNewSection] = useState('');
  const [addTo, setAddTo] = useState(draft.gallery_sections[0] ?? '');
  const fileInput = useRef<HTMLInputElement>(null);
  const { upload, busy, error: uploadError } = useUpload();
  const [progress, setProgress] = useState<string | null>(null);

  const setSlot = (slot: string, name: string | null) => {
    const next = { ...draft.photos };
    if (name) next[slot] = name;
    else delete next[slot];
    set('photos', next);
  };
  const setGallery = (g: GalleryPhoto[]) => set('gallery', g);
  const move = (i: number, d: -1 | 1) => {
    const g = [...draft.gallery];
    const j = i + d;
    if (j < 0 || j >= g.length) return;
    [g[i], g[j]] = [g[j], g[i]];
    setGallery(g);
  };
  const addSection = () => {
    const name = newSection.trim();
    if (!name || draft.gallery_sections.includes(name)) return;
    set('gallery_sections', [...draft.gallery_sections, name]);
    setNewSection('');
  };
  const renameSection = (old: string, name: string) => {
    set('gallery_sections', draft.gallery_sections.map((s) => (s === old ? name : s)));
    setGallery(draft.gallery.map((g) => (g.section === old ? { ...g, section: name } : g)));
    if (addTo === old) setAddTo(name);
  };
  const removeSection = (name: string) => {
    if (draft.gallery.some((g) => g.section === name) && !window.confirm(`Remove "${name}" and its photos from the gallery?`)) return;
    set('gallery_sections', draft.gallery_sections.filter((s) => s !== name));
    setGallery(draft.gallery.filter((g) => g.section !== name));
  };
  const moveSection = (i: number, d: -1 | 1) => {
    const s = [...draft.gallery_sections];
    const j = i + d;
    if (j < 0 || j >= s.length) return;
    [s[i], s[j]] = [s[j], s[i]];
    set('gallery_sections', s);
  };
  const addPhotos = async (files: FileList) => {
    const added: GalleryPhoto[] = [];
    let n = 0;
    for (const f of Array.from(files)) {
      setProgress(`Uploading ${++n} of ${files.length}…`);
      const name = await upload(f);
      if (name) added.push({ photo: name, section: addTo, caption: '' });
    }
    setProgress(null);
    if (added.length) setGallery([...draft.gallery, ...added]);
  };

  return (
    <div className="flex flex-col gap-6">
      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}

      {GROUPS.map((group) => (
        <section key={group}>
          <h3 className="text-card-title mb-2">{group}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
            {PHOTO_SLOTS.filter((s) => s.group === group).map((s) => (
              <PhotoPicker key={s.slot} label={s.label} value={draft.photos[s.slot]} fallback={s.fallback} disabled={!canEdit}
                maxSide={s.group === 'Partners' ? 600 : 1920} onChange={(name) => setSlot(s.slot, name)} />
            ))}
          </div>
        </section>
      ))}

      <section className="flex flex-col gap-3">
        <h3 className="text-card-title">Website gallery</h3>
        <p className="text-secondary text-ink-500 -mt-2">Sections in the order shown on the website. A section with no photos keeps its "Photo coming soon" tiles.</p>
        <div className="flex flex-col gap-1.5 max-w-2xl">
          {draft.gallery_sections.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <ILMSInput disabled={!canEdit} value={s} maxLength={60} onChange={(e) => renameSection(s, e.target.value)} />
              <span className="text-helper-text text-ink-400 w-16 shrink-0 text-right">{draft.gallery.filter((g) => g.section === s).length} photos</span>
              {canEdit && (
                <>
                  <ILMSButton size="sm" variant="ghost" aria-label="Move up" icon={<ArrowUp />} onClick={() => moveSection(i, -1)} />
                  <ILMSButton size="sm" variant="ghost" aria-label="Move down" icon={<ArrowDown />} onClick={() => moveSection(i, 1)} />
                  <ILMSButton size="sm" variant="ghost" aria-label="Remove section" icon={<Trash2 />} onClick={() => removeSection(s)} />
                </>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="flex items-center gap-1.5">
              <ILMSInput placeholder="New section, e.g. Festivals" value={newSection} maxLength={60} onChange={(e) => setNewSection(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSection(); } }} />
              <ILMSButton size="sm" variant="outline" icon={<Plus />} onClick={addSection}>Add section</ILMSButton>
            </div>
          )}
        </div>

        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-label">Add photos to</span>
            <ILMSSelect value={addTo} onChange={(e) => setAddTo(e.target.value)} className="min-w-[200px]">
              {draft.gallery_sections.map((s) => <option key={s} value={s}>{s}</option>)}
            </ILMSSelect>
            <ILMSButton icon={<ImagePlus />} loading={busy} disabled={!addTo} onClick={() => fileInput.current?.click()}>Choose photos…</ILMSButton>
            {progress && <span className="text-helper-text text-ink-500">{progress}</span>}
            {uploadError && <span className="text-error-text text-critical-600">{uploadError}</span>}
            <input ref={fileInput} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
              onChange={(e) => { const f = e.target.files; if (f?.length) addPhotos(f); e.target.value = ''; }} />
          </div>
        )}

        {draft.gallery.length === 0 ? (
          <p className="text-secondary text-ink-400">No gallery photos yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {draft.gallery.map((g, i) => (
              <div key={g.photo} className="rounded-md border border-ink-300/20 bg-white overflow-hidden">
                <img src={mediaUrl(g.photo)} alt="" className="aspect-[4/3] w-full object-cover" />
                <div className="p-2 flex flex-col gap-1.5">
                  <ILMSInput placeholder="Caption (optional)" disabled={!canEdit} maxLength={120} value={g.caption}
                    onChange={(e) => setGallery(draft.gallery.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))} />
                  <ILMSSelect disabled={!canEdit} value={g.section} onChange={(e) => setGallery(draft.gallery.map((x, j) => (j === i ? { ...x, section: e.target.value } : x)))}>
                    {draft.gallery_sections.map((s) => <option key={s} value={s}>{s}</option>)}
                  </ILMSSelect>
                  {canEdit && (
                    <div className="flex gap-1">
                      <ILMSButton size="sm" variant="ghost" aria-label="Earlier" icon={<ArrowUp />} onClick={() => move(i, -1)} />
                      <ILMSButton size="sm" variant="ghost" aria-label="Later" icon={<ArrowDown />} onClick={() => move(i, 1)} />
                      <span className="flex-1" />
                      <ILMSButton size="sm" variant="danger" icon={<Trash2 />} onClick={() => setGallery(draft.gallery.filter((_, j) => j !== i))}>Remove</ILMSButton>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <SaveBar canEdit={canEdit} dirty={dirty} saving={saving} saved={saved} onSave={save} saveLabel="Save photos" />
    </div>
  );
}
